import { useState } from "react";
import { LifeBuoy, Star, Pencil, Trash2, Plane, BedDouble } from "lucide-react";
import styles from "./SupportPackages.module.css";
import SupportLevelModal from "../../../components/System&Settings/SupportPackages/SupportLevelModal";
import SelectBox from "../../../components/BaseComponents/SelectBox/SelectBox";
import DeleteConfirmModal from "../../../components/BaseComponents/DeleteConfirmModal/DeleteConfirmModal";
import { useToast } from "../../../context/ToastContext";
import {
  useSupportLevelsList,
  useActiveSupportServices,
  useMutation,
  getSupportLevel,
  createSupportLevel,
  updateSupportLevel,
  deleteSupportLevel,
} from "../../../api/support-packages/useSupportPackages";

/**
 * Support Levels overview (Support spec v1.0 §6.1).
 *
 * §6.1 fixes the columns: Order, Level, Code, Product, Price, Default, Status, Actions, with
 * Search / Product / Status filters.
 *
 * The Product, Price and Default columns each show BOTH products rather than one value, because
 * §3.1 configures them per product: a level can be EUR 0 on Flight Only and EUR 29.99 on
 * Flight + Hotel, and a single "price" column would have to pick one and mislead about the
 * other.
 */

const PRODUCTS = [
  { key: "FLIGHT_ONLY", short: "Flight Only", Icon: Plane },
  { key: "FLIGHT_HOTEL", short: "Flight + Hotel", Icon: BedDouble },
];

const money = (n) =>
  n == null ? "—" : `EUR ${Number(n).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const unitLabel = (u) => (u === "PER_PERSON" ? "p.p." : u === "PER_BOOKING" ? "per booking" : "");

const SupportLevels = () => {
  const { showToast } = useToast();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [product, setProduct] = useState("");
  const [status, setStatus] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const { items, total, loading, error, refetch } = useSupportLevelsList({
    search: search || undefined,
    product: product || undefined,
    status: status || undefined,
    limit: 200,
  });

  const catalogue = useActiveSupportServices();
  const create = useMutation(createSupportLevel);
  const update = useMutation(updateSupportLevel);
  const remove = useMutation(deleteSupportLevel);

  /**
   * The list response already carries products and services, but not `used`, which decides
   * whether the code box is locked (§7). One read before opening the form is cheaper than
   * putting a usage query behind every row of the list.
   */
  const openEdit = async (row) => {
    setLoadingEdit(true);
    try {
      const full = await getSupportLevel(row.id);
      setEditing(full);
      setModalOpen(true);
    } catch {
      // Falling back to the list row keeps the form usable; the server still refuses a code
      // change on a used level, so the only thing lost is the early warning.
      setEditing(row);
      setModalOpen(true);
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleSave = async (payload) => {
    try {
      if (editing) await update.execute(editing.id, payload);
      else await create.execute(payload);
      showToast(editing ? "Support level updated" : "Support level created", "success");
      setModalOpen(false);
      setEditing(null);
      refetch();
    } catch (err) {
      // The server's wording is the useful one: a duplicate code, a frozen code, or the same
      // service assigned twice for one product.
      showToast(err.message, "error");
    }
  };

  const handleDelete = async () => {
    // Throwing keeps the confirm dialog open, which is right when the reason is "this level has
    // been used on a booking" rather than a transient failure.
    await remove.execute(toDelete.id).catch((err) => {
      showToast(err.message, "error");
      throw err;
    });
    showToast("Support level deleted", "success");
    setToDelete(null);
    refetch();
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.manager}>
          <LifeBuoy size={20} />
          Support Levels
        </div>
        <button
          className={styles.add}
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
          disabled={loading || loadingEdit}
        >
          + Add Level
        </button>
      </div>

      <div className={styles.infoBar}>
        <span className={styles.infoIcon} aria-hidden>ℹ</span>
        <span>
          Each level is configured separately for Flight Only and Flight + Hotel: a level can be
          sold for one product or both, at different prices. One level per product is the
          default.
        </span>
      </div>

      <div className={styles.filterBar}>
        <form
          className={styles.searchForm}
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(searchInput.trim());
          }}
        >
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by name, code or title…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            disabled={loading}
          />
          <button type="submit" className={styles.addbtn} disabled={loading}>
            Search
          </button>
        </form>
        <SelectBox
          placeholder="Product: All"
          value={product}
          options={[
            { label: "All products", value: "" },
            { label: "Flight Only", value: "FLIGHT_ONLY" },
            { label: "Flight + Hotel", value: "FLIGHT_HOTEL" },
          ]}
          disabled={loading}
          onChange={(e) => setProduct(e.target.value)}
        />
        <SelectBox
          placeholder="Status: All"
          value={status}
          options={[
            { label: "All", value: "" },
            { label: "Active", value: "active" },
            { label: "Inactive", value: "inactive" },
          ]}
          disabled={loading}
          onChange={(e) => setStatus(e.target.value)}
        />
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading support levels…</div>
      ) : (
        <>
          <div className={styles.resultsSummary}>
            Showing {items.length} of {total} support levels
          </div>
          <div className={styles.tableSection}>
            <div className={styles.tableCard}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>ORDER</th>
                    <th>LEVEL</th>
                    <th>CODE</th>
                    <th>PRODUCT &amp; PRICE</th>
                    <th>SERVICES</th>
                    <th>STATUS</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={7} className={styles.noResults}>
                        No support levels found.
                      </td>
                    </tr>
                  ) : (
                    items.map((row) => (
                      <tr key={row.id}>
                        <td className={styles.orderNum}>{row.sortOrder}</td>
                        <td>
                          <div className={styles.levelName}>{row.customerTitle}</div>
                          <div className={styles.muted}>{row.name}</div>
                          {row.badge && <span className={styles.badgePill}>{row.badge}</span>}
                        </td>
                        <td className={styles.code}>{row.code}</td>

                        {/* Both products, always, so an unsold product reads as "not sold"
                            rather than as a price of zero. */}
                        <td>
                          <div className={styles.productLines}>
                            {PRODUCTS.map(({ key, short, Icon }) => {
                              const p = row.products?.[key];
                              return (
                                <div key={key} className={styles.productLine}>
                                  <Icon size={13} className={styles.productIcon} />
                                  <span className={styles.productLabel}>{short}</span>
                                  {p?.enabled ? (
                                    <>
                                      <span className={styles.price}>{money(p.price)}</span>
                                      <span className={styles.unit}>{unitLabel(p.pricingUnit)}</span>
                                      {p.isDefault && (
                                        <span className={styles.defaultBadge}>
                                          <Star size={10} /> Default
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    <span className={styles.notSold}>not sold</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </td>

                        <td className={styles.muted}>
                          {(() => {
                            const s = row.services ?? [];
                            if (!s.length) return "—";
                            const inc = s.filter((x) => x.status === "INCLUDED").length;
                            const paid = s.filter((x) => x.status === "PAID").length;
                            const not = s.filter((x) => x.status === "NOT_INCLUDED").length;
                            return (
                              <span className={styles.serviceCounts}>
                                {inc > 0 && <span className={styles.cIncluded}>{inc} included</span>}
                                {paid > 0 && <span className={styles.cPaid}>{paid} paid</span>}
                                {not > 0 && <span className={styles.cNot}>{not} not included</span>}
                              </span>
                            );
                          })()}
                        </td>

                        <td>
                          <span className={row.active ? styles.statusOn : styles.statusOff}>
                            {row.active ? "Active" : "Inactive"}
                          </span>
                        </td>

                        <td className={styles.actionsCell}>
                          <button className={styles.iconBtn} title="Edit" onClick={() => openEdit(row)} disabled={loadingEdit}>
                            <Pencil size={15} />
                          </button>
                          <button
                            className={`${styles.iconBtn} ${styles.danger}`}
                            title="Delete"
                            onClick={() => setToDelete(row)}
                          >
                            <Trash2 size={15} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {modalOpen && (
        <SupportLevelModal
          level={editing}
          services={catalogue}
          saving={create.loading || update.loading}
          onClose={() => {
            setModalOpen(false);
            setEditing(null);
          }}
          onSave={handleSave}
        />
      )}

      <DeleteConfirmModal
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        title="Delete support level"
        message={
          toDelete
            ? `Delete "${toDelete.customerTitle}"? This cannot be undone. A level that has been used on a booking cannot be deleted - deactivate it instead.`
            : ""
        }
        loading={remove.loading}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default SupportLevels;
